import { BaseApi, type RequestHeaders, type RequestParams } from './base';

/**
 * Google Sheets token parameters required for authenticated API calls
 */
export interface GoogleSheetsTokenParams {
  accessToken: string;
  refreshToken: string;
  expiryDate: number;
}

/**
 * Google Sheet item from the sheets list
 */
export interface GoogleSheet {
  id: string;
  name: string;
}

/**
 * Sub-sheet (worksheet/tab) properties
 */
export interface SubSheetProperties {
  sheetId: number;
  title: string;
  index: number;
  sheetType: string;
  gridProperties?: {
    rowCount: number;
    columnCount: number;
  };
  tabColor?: Record<string, number>;
  tabColorStyle?: Record<string, any>;
}

/**
 * Sub-sheet item from the sub-sheets list
 */
export interface SubSheet {
  properties: SubSheetProperties;
}

/**
 * Response shape for sheets list
 */
export interface SheetsListResponse {
  data: {
    count: number;
    sheets: GoogleSheet[];
  };
  message: string;
}

/**
 * Response shape for sub-sheets list
 */
export interface SubSheetsListResponse {
  data: {
    count: number;
    sheets: SubSheet[];
  };
  message: string;
}

/**
 * Response shape for sub-sheet data (column headers)
 */
export interface SubSheetDataResponse {
  data: {
    sheetName: string;
    range: string;
    values: string[];
  };
  message: string;
}

/**
 * Response shape for auth URL
 */
export interface AuthUrlResponse {
  data: string;
  message: string;
}

/**
 * Google Sheets API service
 * Handles OAuth, fetching sheets, sub-sheets, and column headers
 */
class GoogleSheetsApi extends BaseApi {
  constructor() {
    super('');
  }

  /**
   * Get the Google OAuth authorization URL
   * Opens a popup for the user to sign in and grant permissions
   */
  public async getAuthUrl({
    params,
    headers,
  }: {
    params?: RequestParams;
    headers?: RequestHeaders;
  } = {}): Promise<AuthUrlResponse> {
    return this.request<any, AuthUrlResponse>({
      method: 'get',
      url: 'google-auth/google-sheets-integration/auth-url',
      params,
      headers,
    });
  }

  /**
   * Get all Google Sheets files accessible by the connected account
   */
  public async getSheets(
    tokens: GoogleSheetsTokenParams,
    {
      params,
      headers,
    }: {
      params?: RequestParams;
      headers?: RequestHeaders;
    } = {},
  ): Promise<SheetsListResponse> {
    return this.request<any, SheetsListResponse>({
      method: 'get',
      url: 'flow/google-sheets-integration/sheets',
      params: {
        ...params,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiryDate: tokens.expiryDate,
      },
      headers,
    });
  }

  /**
   * Get all worksheets (tabs) for a specific Google Sheet
   */
  public async getSubSheets(
    sheetId: string,
    tokens: GoogleSheetsTokenParams,
    {
      params,
      headers,
    }: {
      params?: RequestParams;
      headers?: RequestHeaders;
    } = {},
  ): Promise<SubSheetsListResponse> {
    return this.request<any, SubSheetsListResponse>({
      method: 'get',
      url: `flow/google-sheets-integration/sheets/${sheetId}/sub-sheets`,
      params: {
        ...params,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiryDate: tokens.expiryDate,
      },
      headers,
    });
  }

  /**
   * Get column headers (first row data) for a specific worksheet
   */
  public async getSubSheetData(
    sheetId: string,
    subSheetName: string,
    tokens: GoogleSheetsTokenParams,
    {
      params,
      headers,
    }: {
      params?: RequestParams;
      headers?: RequestHeaders;
    } = {},
  ): Promise<SubSheetDataResponse> {
    return this.request<any, SubSheetDataResponse>({
      method: 'get',
      url: `flow/google-sheets-integration/sheets/${sheetId}/sub-sheets/${encodeURIComponent(subSheetName)}/data`,
      params: {
        ...params,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiryDate: tokens.expiryDate,
      },
      headers,
    });
  }
}

export const googleSheetsApi = new GoogleSheetsApi();
export default googleSheetsApi;
